<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="getInputErrors('name')" />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }" class="pb-3">
        <BaseSelectDropdown
          :items="dropdownRouteTypes"
          :selected-item="selectedRouteType"
          :label="$t('routes.typeOfRoute')"
          @update:selected="handleRouteTypeSelect"
          name="routeTypes"
          clearable
          show-error-as-tooltip
          :error="getInputErrors('routeType')" />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }" class="pb-3">
        <BaseSelectDropdown
          :items="dropdownUserTypes"
          :selected-item="selectedUserType"
          :label="$t('routes.typeOfUser')"
          @update:selected="handleUserTypeSelect"
          name="userTypes"
          clearable
          show-error-as-tooltip
          :error="getInputErrors('userType')" />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }" class="pb-3">
        <BaseSelectDropdown
          :items="dropdownSalespersonTypes"
          :selected-item="selectedSalesPersonType"
          :label="$t('routes.typeOfSalesPerson')"
          @update:selected="handleSalesPersonTypeSelect"
          name="salesPersonTypes"
          clearable />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }" class="pb-3">
        <BaseSelectDropdown
          :items="dropdownMwAccountGroups"
          :selected-item="selectedAccountGroup"
          :label="$t('routes.accountGroup')"
          @update:selected="handleAccountGroupSelect"
          name="accountGroups"
          clearable
          show-error-as-tooltip
          :error="getInputErrors('accountGroup')" />
      </div>

      <BaseToggle
        :label="$t('routes.allowWhenUnique')"
        name="allowWhenUnique"
        v-model="modifiedData.allowWhenUnique"
        class="py-1"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive"
        class="py-1"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { addOrEditRouteDefinition } from '@/services/api/routes';
import { deepCopy } from '@/utils/data';
import type { RouteDefinitions } from '@/types/Routes';
import type { SelectDropdownItem } from '@/types/Base';

const { dropdownUserTypes, dropdownSalespersonTypes, dropdownMwAccountGroups, dropdownRouteTypes } =
  storeToRefs(useLookupsStore());

const selectedRouteType = ref<SelectDropdownItem | null>(null);
const selectedUserType = ref<SelectDropdownItem | null>(null);
const selectedSalesPersonType = ref<SelectDropdownItem | null>(null);
const selectedAccountGroup = ref<SelectDropdownItem | null>(null);

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    routeDefinition: RouteDefinitions;
  }>(),
  {}
);

const modifiedData = ref<RouteDefinitions>(deepCopy<RouteDefinitions>(props.routeDefinition));
const isModified = ref<boolean>(false);

onMounted(() => {
  if (modifiedData.value.mwUserTypeId)
    selectedUserType.value =
      dropdownUserTypes.value.find((x) => x.value === modifiedData.value.mwUserTypeId) || null;
  if (modifiedData.value.mwRouteTypeId)
    selectedRouteType.value =
      dropdownRouteTypes.value.find((x) => x.value === modifiedData.value.mwRouteTypeId) || null;
  if (modifiedData.value.mwAccountGroupId)
    selectedAccountGroup.value =
      dropdownMwAccountGroups.value.find((x) => x.value === modifiedData.value.mwAccountGroupId) ||
      null;
  if (modifiedData.value.mwSalesPersonTypeId)
    selectedSalesPersonType.value =
      dropdownSalespersonTypes.value.find(
        (x) => x.value === modifiedData.value.mwSalesPersonTypeId
      ) || null;
});

function onDismissChanges() {
  modifiedData.value = deepCopy<RouteDefinitions>(props.routeDefinition);
  emit('closeModal');
}

const toastStore = useToastStore();

function handleRouteTypeSelect(item: SelectDropdownItem) {
  selectedRouteType.value = item ? item : null;
  modifiedData.value.mwRouteTypeId = item ? item.value.toString() : '';
  modifiedData.value.mwRouteType = item
    ? { id: item.value.toString(), name: item.name }
    : undefined;
}

function handleAccountGroupSelect(item: SelectDropdownItem) {
  selectedAccountGroup.value = item ? item : null;
  modifiedData.value.mwAccountGroupId = item ? item.value.toString() : '';
  modifiedData.value.mwAccountGroup = item
    ? { id: item.value.toString(), accountGroup: item.name }
    : undefined;
}

function handleSalesPersonTypeSelect(item: SelectDropdownItem) {
  selectedSalesPersonType.value = item ? item : null;
  modifiedData.value.mwSalesPersonTypeId = item ? item.value.toString() : '';
}

function handleUserTypeSelect(item: SelectDropdownItem) {
  selectedUserType.value = item ? item : null;
  modifiedData.value.mwUserTypeId = item ? item.value.toString() : '';
  modifiedData.value.mwUserType = item ? { id: item.value.toString(), name: item.name } : undefined;
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditRouteDefinition(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: 'Record already exists' });
  }
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.routeType = newData.mwRouteTypeId || '';
    validationModel.value.userType = newData.mwUserTypeId || '';
    validationModel.value.accountGroup = newData.mwAccountGroupId || '';

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.routeDefinition);
    isModified.value = valueChanged;
  },
  { deep: true }
);

const validationModel = ref({
  name: modifiedData.value.name || '',
  routeType: modifiedData.value.mwRouteTypeId || '',
  userType: modifiedData.value.mwUserTypeId || '',
  accountGroup: modifiedData.value.mwAccountGroupId || '',
});

const rules = computed(() => ({
  name: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  routeType: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  userType: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  accountGroup: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(type: 'name' | 'routeType' | 'userType' | 'accountGroup') {
  if ($v.value[type].required.$invalid) return $v.value[type].required.$message;
  else return '';
}
</script>
